/**
 * RewardSettings — READ-ONLY summary of coin economics for the Finances
 * dashboard. Editing lives at `/admin/tools/coin-settings` (single source
 * of truth). This component was previously an editable duplicate that hit
 * the same endpoint but with a divergent payload shape (query param vs
 * JSON body) — the two implementations drifted, and the Finances copy
 * silently white-screened on save. Now it's a passive read-out with a
 * one-click bridge to the canonical editor.
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Coins, ExternalLink, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RewardSettings = () => {
  const [settings, setSettings] = useState({
    coin_value_inr: null,
    monthly_earning_cap: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/reward-settings`);
        if (cancelled) return;
        setSettings({
          coin_value_inr: res.data?.coin_value_inr ?? null,
          monthly_earning_cap: res.data?.monthly_earning_cap ?? null,
        });
      } catch (e) {
        if (!cancelled) setError('Failed to load reward settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <Card data-testid="reward-settings-readonly-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="flex items-center gap-2">
              <Coins className="w-5 h-5 text-yellow-600" />
              Coin Reward Economics
            </CardTitle>
            <CardDescription className="mt-1">
              Read-only summary. Full configuration lives in Admin Tools.
            </CardDescription>
          </div>
          <Link to="/admin/tools/coin-settings">
            <Button
              variant="outline"
              size="sm"
              className="flex-shrink-0"
              data-testid="reward-settings-manage-link"
            >
              Manage in Admin Tools
              <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
            </Button>
          </Link>
        </div>
      </CardHeader>
      <CardContent>
        {error ? (
          <div className="flex items-center gap-2 text-sm text-rose-700">
            <AlertCircle className="w-4 h-4" />
            {error}
          </div>
        ) : loading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : (
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500 font-medium">
                Current coin value
              </dt>
              <dd
                className="mt-1 text-lg font-semibold text-slate-900"
                data-testid="reward-settings-coin-value"
              >
                1 coin = ₹{settings.coin_value_inr ?? '—'}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500 font-medium">
                Monthly earning cap
              </dt>
              <dd
                className="mt-1 text-lg font-semibold text-slate-900"
                data-testid="reward-settings-monthly-cap"
              >
                {settings.monthly_earning_cap != null
                  ? `${settings.monthly_earning_cap} coins`
                  : '—'}
              </dd>
            </div>
          </dl>
        )}
      </CardContent>
    </Card>
  );
};

export default RewardSettings;
